import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { type FC } from "react";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import CasinoProviderSelect from "../../CreateSeason/ChallengesAndTasks/CasinoProviderSelect";
import { Input, Select } from "../../../shared";
import {
  MONTHLY_CASINO_ACTION_OPTIONS,
  sanitizeDecimalInput,
  type BetCurrency,
  type LabeledOption,
  type MonthlyCasinoAction,
} from "../monthlyChallengeData";

import styles from "./MonthlyCasinoPicksFields.module.css";

/** Partial draft update the card merges into the challenge. Scoped to the
 *  casino fields; providers and games are independent selections (a game can be
 *  picked without its whole provider), matching the Season casino picker. */
export type MonthlyCasinoPicksPatch = {
  casinoProviders?: ReadonlyArray<string>;
  casinoGames?: ReadonlyArray<string>;
  casinoGameLabels?: Readonly<Record<string, string>>;
  casinoAction?: MonthlyCasinoAction;
  casinoMinBet?: string;
  wagerCurrency?: BetCurrency;
};

export type MonthlyCasinoPicksFieldsProps = {
  /** Selected provider names (multiple allowed). */
  providers: ReadonlyArray<string>;
  /** Individually-selected game ids, chosen across any provider. */
  games: ReadonlyArray<string>;
  /** Display names for {@link games}, keyed by game id, so chips stay readable
   *  after the picker closes. */
  gameLabels: Readonly<Record<string, string>>;
  action: MonthlyCasinoAction;
  /** Minimum wager a play must meet to count; free-form string ("" = no
   *  minimum), sanitised to a money-style decimal on change. */
  minBet: string;
  /** Currency a qualifying wager must be placed in (required). `undefined`
   *  until the admin picks one. */
  wagerCurrency: BetCurrency | undefined;
  /** Currency options sourced from `GET /config` (same catalogue as the Season
   *  task form); empty while the config is loading. */
  wagerCurrencyOptions: ReadonlyArray<LabeledOption<BetCurrency>>;
  onChange: (patch: MonthlyCasinoPicksPatch) => void;
  /** Read-only mode (locked / Active view): disables the picker, the Action
   *  pills and the Minimum Bet Amount input so the casino picks render
   *  inspect-only. */
  disabled?: boolean;
  idPrefix: string;
};

/**
 * The "Casino Picks" task-type field set (FE-2298): a Play / Win Action toggle,
 * the shared {@link CasinoProviderSelect} picker (multiple providers + games
 * across them, with cross-provider search and a muted provider label beside
 * each game), and the required Minimum Bet Amount input. Rendered only while
 * Casino Picks is the selected task type, so the casino catalogue queries fire
 * lazily. Selection is lifted to the challenge draft via `onChange`; this
 * component owns no state.
 *
 * Returns the cells as a fragment (Action → Provider and Games → Wager
 * Currency → Minimum Bet Amount) so the card lays them out with the Task Type
 * dropdown in one shared 2-column grid: `Task Type | Action` / `Provider and
 * Games` (full width) / `Wager Currency | Minimum Bet Amount`.
 */
const MonthlyCasinoPicksFields: FC<MonthlyCasinoPicksFieldsProps> = ({
  providers,
  games,
  gameLabels,
  action,
  minBet,
  wagerCurrency,
  wagerCurrencyOptions,
  onChange,
  disabled = false,
  idPrefix,
}) => (
  <>
    <div className={styles.field}>
      <span className={styles.fieldLabel}>
        {AppStrings.MONTHLY_CHALLENGE_ACTION_LABEL}
      </span>
      <div
        className={styles.pills}
        role="radiogroup"
        aria-label={AppStrings.MONTHLY_CHALLENGE_ACTION_LABEL}
        data-testid={`${idPrefix}-action`}
      >
        {MONTHLY_CASINO_ACTION_OPTIONS.map((option) => {
          const isActive = option.value === action;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              disabled={disabled}
              className={`${styles.pill} ${isActive ? styles.pillActive : ""}`.trim()}
              onClick={() => onChange({ casinoAction: option.value })}
              data-testid={`${idPrefix}-action-${option.value.toLowerCase()}`}
            >
              {AppStrings[option.labelKey]}
            </button>
          );
        })}
      </div>
    </div>

    <div
      className={`${styles.field} ${styles.providersField}`}
      data-testid={`${idPrefix}-casino-fields`}
    >
      <span className={styles.fieldLabel}>
        {AppStrings.CREATE_TASK_CASINO_PROVIDERS}
        <Tooltip title={AppStrings.MONTHLY_CHALLENGE_PROVIDER_INFO}>
          <InfoCircleOutlined
            className={styles.infoIcon}
            aria-label={AppStrings.MONTHLY_CHALLENGE_PROVIDER_INFO}
          />
        </Tooltip>
      </span>
      <CasinoProviderSelect
        providers={[...providers]}
        games={[...games]}
        gameLabels={gameLabels}
        onChangeProviders={(next) => onChange({ casinoProviders: next })}
        onChangeGames={(next) => onChange({ casinoGames: next })}
        onChangeGameLabels={(next) => onChange({ casinoGameLabels: next })}
        disabled={disabled}
        data-testid={`${idPrefix}-casino-providers`}
      />
    </div>

    <label className={styles.field}>
      <span className={styles.fieldLabel}>
        {AppStrings.MONTHLY_CHALLENGE_WAGER_CURRENCY_LABEL}
        <Tooltip title={AppStrings.MONTHLY_CHALLENGE_WAGER_CURRENCY_INFO}>
          <InfoCircleOutlined
            className={styles.infoIcon}
            aria-label={AppStrings.MONTHLY_CHALLENGE_WAGER_CURRENCY_INFO}
          />
        </Tooltip>
      </span>
      <Select<BetCurrency>
        className={styles.select}
        value={wagerCurrency}
        options={[...wagerCurrencyOptions]}
        placeholder={AppStrings.MONTHLY_CHALLENGE_WAGER_CURRENCY_PLACEHOLDER}
        aria-label={AppStrings.MONTHLY_CHALLENGE_WAGER_CURRENCY_LABEL}
        disabled={disabled}
        onChange={(value) => onChange({ wagerCurrency: value })}
        data-testid={`${idPrefix}-wager-currency`}
      />
    </label>

    <label className={styles.field}>
      <span className={styles.fieldLabel}>
        {AppStrings.MONTHLY_CHALLENGE_MIN_BET_LABEL}
        <Tooltip title={AppStrings.MONTHLY_CHALLENGE_MIN_BET_INFO}>
          <InfoCircleOutlined
            className={styles.infoIcon}
            aria-label={AppStrings.MONTHLY_CHALLENGE_MIN_BET_INFO}
          />
        </Tooltip>
      </span>
      <Input
        className={styles.minBetInput}
        value={minBet}
        placeholder={AppStrings.MONTHLY_CHALLENGE_MIN_BET_PLACEHOLDER}
        aria-label={AppStrings.MONTHLY_CHALLENGE_MIN_BET_LABEL}
        disabled={disabled}
        onChange={(e) =>
          onChange({ casinoMinBet: sanitizeDecimalInput(e.target.value) })
        }
        data-testid={`${idPrefix}-min-bet`}
      />
    </label>
  </>
);

export default MonthlyCasinoPicksFields;
